import { forwardRef, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { Autocomplete } from '@mui/material';
import { provinceCodesOfCountryCode, provinceNameByCode } from '~/adapters/geography/countriesAndProvinces';
import { AutofillTextField, AutofillTextFieldProps } from '../AutofillTextField';
import { ValidatedAddressDialogFieldProps } from '../../types';
import { useErrorProps } from '../useErrorProps';
import { tooLongErrorMessage } from '../constants';

/* eslint-disable react/jsx-props-no-spreading */

type AddressRegionTextFieldProps = Omit<AutofillTextFieldProps, 'autoFillKey' | 'label'>;

const AddressRegionTextField = forwardRef<HTMLDivElement, AddressRegionTextFieldProps>((props, ref) => (
    <AutofillTextField ref={ref} {...props} autoFillKey="address-level1" label="State / Province" />
));

// see AddressCountry.tsx for notes on MUI Autocomplete usage
export const AddressRegion = ({ control, fieldErrors }: ValidatedAddressDialogFieldProps) => {
    const { error, helperText } = useErrorProps('addressRegion', fieldErrors);

    const addressCountryWatcher = useWatch({ control, name: 'addressCountry' });
    const regionCodesForCountry = useMemo(
        () => provinceCodesOfCountryCode(addressCountryWatcher ?? ''),
        [addressCountryWatcher],
    );

    return (
        <Controller
            name="addressRegion"
            control={control}
            rules={{ maxLength: { value: 50, message: tooLongErrorMessage } }}
            render={({ field: { value, onChange, ...field } }) =>
                regionCodesForCountry.length ? (
                    // if there are options available, show Autocomplete, otherwise plain TextField
                    <Autocomplete
                        autoSelect
                        autoHighlight
                        options={regionCodesForCountry}
                        getOptionLabel={(option) => provinceNameByCode[option] ?? option}
                        value={value}
                        onChange={(_event, newValue) => onChange(newValue)}
                        renderInput={(params) => (
                            <AddressRegionTextField {...params} placeholder="Select State or Province" />
                        )}
                        {...field}
                    />
                ) : (
                    <AddressRegionTextField
                        {...field}
                        value={value}
                        onChange={onChange}
                        error={error}
                        helperText={helperText}
                        placeholder="Enter State or Province"
                        fullWidth
                    />
                )
            }
        />
    );
};
